import { Dialog, IconButton } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { TbKey, TbLink, TbLinkOff, TbPlus, TbRefresh, TbTrash } from "react-icons/tb";
import {
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
} from "../../../../../Components/Molecules/GridHistory";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useToast } from "../../../../../Hooks/toast";
import {
  fetchBaasAccounts,
  isPixKeyLinkableTargetAccount,
} from "../../../../../Services/baasAccountsListApi";
import {
  createPixKey,
  deletePixKey,
  linkPixKey,
  listPixKeys,
  unlinkPixKey,
} from "../../../../../Services/baasPixApi";
import {
  isBaasExitRailVirtualAccount,
  isBaasOriginRailAccount,
  isIssuerFundingPixLinkLabel,
  isVirtualInstanceShellAccount,
} from "../../../../../Services/baasVirtualShells";
import { isPixEligibleAccount } from "../../../../../Utils/accountPixEligibility";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-2xl !max-h-[85vh]";

/** Tipo | Chave (wide) | Status | Ações — tighter status/actions. */
const PIX_KEYS_GRID_COLS =
  "lg:grid-cols-[minmax(0,0.65fr)_minmax(0,2.6fr)_minmax(0,0.65fr)_minmax(0,0.7fr)]";

const isAvailableToLinkPixKey = (pixKey) =>
  Boolean(pixKey?.available_to_link || pixKey?.availableToLink);

const isLinkedPixKey = (pixKey) => {
  if (isAvailableToLinkPixKey(pixKey)) return false;
  return Boolean(
    pixKey?.linked
      || pixKey?.Linked
      || ((pixKey?.origin_account_id || pixKey?.originAccountId)
        && (pixKey?.linked_account_id || pixKey?.linkedAccountId)),
  );
};

const resolveKeyId = (pixKey) => pixKey?.key_id || pixKey?.keyId || "";

const resolveOriginAccountId = (pixKey, fallbackAccountId) =>
  pixKey?.origin_account_id || pixKey?.originAccountId || fallbackAccountId || "";

const shortAccountLabel = (label) => {
  const raw = String(label || "").trim();
  if (!raw) return "";
  return raw.split(" · ")[0].trim() || raw;
};

const resolveOriginLabel = (pixKey) =>
  shortAccountLabel(
    pixKey?.origin_account_label || pixKey?.originAccountLabel || "",
  );

const resolveLinkedLabel = (pixKey) =>
  shortAccountLabel(
    pixKey?.linked_account_label || pixKey?.linkedAccountLabel || "",
  );

const accountLabel = (account) =>
  account?.entityName || account?.name || account?.alias || account?.id || "—";

/** Compact single-line badge for linked / available states. */
const resolvePixKeyBadge = (pixKey, { virtualMode } = {}) => {
  if (isAvailableToLinkPixKey(pixKey)) {
    const origin = resolveOriginLabel(pixKey);
    return {
      text: origin
        ? intl.get("baas.pix.keys.availableToLinkShort", { name: origin })
        : intl.get("baas.pix.keys.availableToLinkBadge"),
      title: origin
        ? `${intl.get("baas.pix.keys.availableToLinkBadge")} · ${origin}`
        : intl.get("baas.pix.keys.availableToLinkBadge"),
    };
  }
  if (!isLinkedPixKey(pixKey)) return null;

  const linkedDest = resolveLinkedLabel(pixKey);
  const origin = resolveOriginLabel(pixKey);
  const fullLinkedLabel =
    pixKey?.linked_account_label || pixKey?.linkedAccountLabel || "";
  const fullOriginLabel =
    pixKey?.origin_account_label || pixKey?.originAccountLabel || "";

  if (
    isIssuerFundingPixLinkLabel(fullLinkedLabel)
    || isIssuerFundingPixLinkLabel(linkedDest)
    || isIssuerFundingPixLinkLabel(fullOriginLabel)
    || isIssuerFundingPixLinkLabel(origin)
  ) {
    return {
      text: intl.get("baas.pix.keys.fundingKeyBadge"),
      title: intl.get("baas.pix.keys.fundingKeyBadgeHint"),
    };
  }

  if (!virtualMode && linkedDest) {
    return {
      text: intl.get("baas.pix.keys.linkedAwayShort", { name: linkedDest }),
      title: intl.get("baas.pix.keys.linkedAwayShort", { name: linkedDest }),
    };
  }
  if (virtualMode && origin) {
    return {
      text: intl.get("baas.pix.keys.linkedFromShort", { name: origin }),
      title: intl.get("baas.pix.keys.linkedFromShort", { name: origin }),
    };
  }
  return {
    text: intl.get("baas.pix.keys.linkedBadgeShort"),
    title: intl.get("baas.pix.keys.linkedBadge"),
  };
};

const isMatrizPixKeyOwnerAccount = (item) => {
  if (!item?.id) return false;
  if (isBaasExitRailVirtualAccount(item) || isBaasOriginRailAccount(item)) return false;
  if (isVirtualInstanceShellAccount(item)) return false;
  const type = String(item?.type || "")
    .trim()
    .toLowerCase();
  return type === "operational";
};

const pickParentMatriz = (matrices, account) => {
  if (!Array.isArray(matrices) || matrices.length === 0) return null;
  if (matrices.length === 1) return matrices[0];

  const entityId = String(account?.entityId || "").trim();
  const sameEntity = entityId
    ? matrices.filter((item) => String(item.entityId || "").trim() === entityId)
    : matrices;
  const pool = sameEntity.length > 0 ? sameEntity : matrices;

  return [...pool].sort((a, b) => {
    const aPartner = a.hasPartnerAccount || a.partnerAccountId ? 1 : 0;
    const bPartner = b.hasPartnerAccount || b.partnerAccountId ? 1 : 0;
    if (bPartner !== aPartner) return bPartner - aPartner;
    return String(a.id).localeCompare(String(b.id));
  })[0];
};

const PixKeysHeader = () => (
  <GridHistoryHeader colSpan={4} className="lg:!min-h-9 lg:!py-1.5">
    <span>{intl.get("baas.pix.keys.columns.type")}</span>
    <span>{intl.get("baas.pix.keys.columns.key")}</span>
    <span>{intl.get("baas.pix.keys.columns.status")}</span>
    <span className="text-right">{intl.get("baas.accounts.columns.actions")}</span>
  </GridHistoryHeader>
);

const PixKeyRow = ({
  pixKey,
  onDelete,
  onLink,
  onUnlink,
  disabled,
  canWrite,
  linkingKeyId,
  virtualMode,
}) => {
  const availableToLink = isAvailableToLinkPixKey(pixKey);
  const linked = isLinkedPixKey(pixKey);
  const keyId = resolveKeyId(pixKey);
  const busy = Boolean(disabled || (linkingKeyId && linkingKeyId === keyId));
  const keyValue = pixKey.key || "—";
  const badge = resolvePixKeyBadge(pixKey, { virtualMode });

  return (
    <GridHistoryRow colSpan={4} className="!gap-1.5 !py-2 lg:!py-1.5">
      <GridHistoryCell label={intl.get("baas.pix.keys.columns.type")}>
        <p className="truncate text-xs font-semibold text-brand-dark dark:text-gray-100">
          {pixKey.key_type || pixKey.keyType || "—"}
        </p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.pix.keys.columns.key")}>
        <p
          className="truncate font-mono text-xs text-gray-600 dark:text-gray-300"
          title={keyValue}
        >
          {keyValue}
        </p>
        {badge ? (
          <p
            className="mt-0.5 truncate text-[10px] font-medium tracking-wide text-[#a66f1d] dark:text-brand-gold-mid"
            title={badge.title}
          >
            {badge.text}
          </p>
        ) : null}
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.pix.keys.columns.status")}>
        <p className="truncate text-xs text-gray-600 dark:text-gray-300">
          {pixKey.status || "—"}
        </p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.actions")} align="right">
        {canWrite ? (
          <div className="inline-flex items-center gap-0.5">
            {linked ? (
              <button
                type="button"
                className="grid-history-action-btn grid-history-action-btn--icon !h-7 !w-7 !min-h-7 !min-w-7"
                aria-label={intl.get("baas.pix.keys.unlink")}
                title={intl.get("baas.pix.keys.unlink")}
                onClick={() => onUnlink(pixKey)}
                disabled={busy || !keyId}
              >
                <TbLinkOff className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                className="grid-history-action-btn grid-history-action-btn--icon !h-7 !w-7 !min-h-7 !min-w-7"
                aria-label={
                  availableToLink || virtualMode
                    ? intl.get("baas.pix.keys.linkHere")
                    : intl.get("baas.pix.keys.link")
                }
                title={
                  availableToLink || virtualMode
                    ? intl.get("baas.pix.keys.linkHereTitle")
                    : intl.get("baas.pix.keys.linkTitle")
                }
                onClick={() => onLink(pixKey)}
                disabled={busy || !keyId}
              >
                <TbLink className="h-3.5 w-3.5" />
              </button>
            )}
            {!linked && !availableToLink && !virtualMode ? (
              <button
                type="button"
                className="grid-history-action-btn grid-history-action-btn--icon !h-7 !w-7 !min-h-7 !min-w-7"
                aria-label={intl.get("baas.pix.keys.delete")}
                onClick={() => onDelete(keyId)}
                disabled={busy || !keyId}
              >
                <TbTrash className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </GridHistoryCell>
    </GridHistoryRow>
  );
};

const VirtualKeySection = ({
  title,
  emptyMessage,
  keys,
  onDelete,
  onLink,
  onUnlink,
  disabled,
  canWrite,
  linkingKeyId,
}) => (
  <section className="space-y-1.5">
    <h3 className="text-xs font-bold text-brand-dark dark:text-gray-100">{title}</h3>
    <div className="app-card overflow-hidden">
      {keys.length === 0 ? (
        <div className="app-empty-state m-3 !py-4 text-xs">{emptyMessage}</div>
      ) : (
        <GridHistoryContainer gridCols={PIX_KEYS_GRID_COLS} className="lg:!gap-x-3 lg:!px-3">
          <PixKeysHeader />
          {keys.map((key) => (
            <PixKeyRow
              key={resolveKeyId(key) || key.key}
              pixKey={key}
              onDelete={onDelete}
              onLink={onLink}
              onUnlink={onUnlink}
              disabled={disabled}
              canWrite={canWrite}
              linkingKeyId={linkingKeyId}
              virtualMode
            />
          ))}
        </GridHistoryContainer>
      )}
    </div>
  </section>
);

/**
 * Modal for account Pix keys via `/v1/ib/console/v1/pix/accounts/{id}/keys`.
 * Parent must only mount when instance product `pix` is enabled.
 * Instance shells (OPERATING / reserves / OTC / ENTITY ROOT litter) cannot create keys.
 * Linked keys stay visible on the matriz with destination label; virtuals show linked + available.
 */
export const BaasAccountPixKeysModal = ({ open, onClose, accountId, account = null }) => {
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();
  const { can } = useBaasCapabilities();
  const canViewPix = can("processing.pix.view");
  const canWritePix = can("processing.pix.write");
  const isOriginRail = Boolean(account && isBaasOriginRailAccount(account));
  const isVirtualAccount = Boolean(account && isBaasExitRailVirtualAccount(account));
  const canCreateKey = Boolean(
    canWritePix
      && account
      && isPixEligibleAccount(account)
      && !isVirtualAccount
      && !isOriginRail,
  );
  const createBlockedForShell = Boolean(
    account && (!isPixEligibleAccount(account) || isVirtualAccount || isOriginRail),
  );
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkTargets, setLinkTargets] = useState([]);
  const [linkTargetsLoading, setLinkTargetsLoading] = useState(false);
  const [linkingKey, setLinkingKey] = useState(null);
  const [selectedTargetId, setSelectedTargetId] = useState("");
  const [linkingKeyId, setLinkingKeyId] = useState("");
  const [formError, setFormError] = useState(null);

  /** Fallback only when API omits available_to_link — single parent matriz, never all matrices. */
  const fetchParentMatrizKeysFallback = useCallback(async () => {
    if (!selectedInstance || !accountId || !account) return [];
    try {
      const all = await fetchBaasAccounts(selectedInstance);
      const entityId = String(account?.entityId || "").trim();
      const matrices = (Array.isArray(all) ? all : []).filter((item) => {
        if (!isMatrizPixKeyOwnerAccount(item)) return false;
        if (String(item.id) === String(accountId)) return false;
        if (entityId && String(item.entityId || "").trim() !== entityId) return false;
        return true;
      });
      const parent = pickParentMatriz(matrices, account);
      if (!parent?.id) return [];

      const matrizKeys = await listPixKeys(selectedInstance, parent.id);
      const label = accountLabel(parent);
      return (Array.isArray(matrizKeys) ? matrizKeys : [])
        .filter((key) => !isLinkedPixKey(key) && !isAvailableToLinkPixKey(key))
        .map((key) => ({
          ...key,
          available_to_link: true,
          origin_account_id: parent.id,
          origin_account_label: label,
        }));
    } catch {
      return [];
    }
  }, [selectedInstance, accountId, account]);

  const refreshKeys = useCallback(async () => {
    if (!selectedInstance || !accountId || !canViewPix) {
      setKeys([]);
      return;
    }
    setLoading(true);
    try {
      let next = await listPixKeys(selectedInstance, accountId);
      if (!Array.isArray(next)) next = [];

      const needsMatrizFallback =
        (isVirtualAccount || isOriginRail)
        && !next.some((key) => isAvailableToLinkPixKey(key));

      if (needsMatrizFallback) {
        const matrizKeys = await fetchParentMatrizKeysFallback();
        const byId = new Map();
        for (const key of [...next, ...matrizKeys]) {
          const id = resolveKeyId(key) || key?.key || "";
          if (!id || byId.has(id)) continue;
          byId.set(id, key);
        }
        next = Array.from(byId.values());
      }

      setKeys(next);
    } catch (error) {
      setKeys([]);
      setFormError(error?.message || intl.get("baas.pix.error.load"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedInstance,
    accountId,
    canViewPix,
    isVirtualAccount,
    isOriginRail,
    fetchParentMatrizKeysFallback,
  ]);

  useEffect(() => {
    if (!open || !canViewPix) {
      setKeys([]);
      setLoading(false);
      setLinkingKey(null);
      setSelectedTargetId("");
      setLinkingKeyId("");
      setFormError(null);
      return undefined;
    }
    setFormError(null);
    refreshKeys();
    return undefined;
  }, [open, canViewPix, refreshKeys]);

  const loadLinkTargets = useCallback(async () => {
    if (!selectedInstance || !accountId) {
      setLinkTargets([]);
      return;
    }
    setLinkTargetsLoading(true);
    try {
      const all = await fetchBaasAccounts(selectedInstance);
      const entityId = String(account?.entityId || "").trim();
      const next = (Array.isArray(all) ? all : []).filter((item) => {
        if (!item?.id || String(item.id) === String(accountId)) return false;
        if (!isPixKeyLinkableTargetAccount(item)) return false;
        if (entityId && String(item.entityId || "").trim() !== entityId) return false;
        return true;
      });
      setLinkTargets(next);
    } catch {
      setLinkTargets([]);
    } finally {
      setLinkTargetsLoading(false);
    }
  }, [selectedInstance, accountId, account?.entityId]);

  const handleCreateEvp = async () => {
    if (!selectedInstance || !accountId || loading || !canCreateKey) return;
    setLoading(true);
    setFormError(null);
    try {
      await createPixKey(selectedInstance, accountId, { key_type: "EVP" });
      addToast({ type: "success", title: intl.get("baas.pix.keys.createSuccess") });
      await refreshKeys();
    } catch (error) {
      setFormError(error?.message || intl.get("baas.pix.error.createKey"));
      setLoading(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!selectedInstance || !accountId || !keyId || loading || !canWritePix) return;
    setLoading(true);
    setFormError(null);
    try {
      await deletePixKey(selectedInstance, accountId, keyId);
      addToast({ type: "success", title: intl.get("baas.pix.keys.deleteSuccess") });
      await refreshKeys();
    } catch (error) {
      setFormError(error?.message || intl.get("baas.pix.error.deleteKey"));
      setLoading(false);
    }
  };

  const handleLinkToThisVirtual = async (pixKey) => {
    const keyId = resolveKeyId(pixKey);
    const originId = resolveOriginAccountId(pixKey, "");
    if (!selectedInstance || !accountId || !keyId || !originId || !canWritePix) return;
    setLinkingKeyId(keyId);
    setLoading(true);
    setFormError(null);
    try {
      await linkPixKey(selectedInstance, originId, keyId, accountId);
      addToast({ type: "success", title: intl.get("baas.pix.keys.linkSuccess") });
      setLinkingKey(null);
      setSelectedTargetId("");
      await refreshKeys();
    } catch (error) {
      setFormError(error?.message || intl.get("baas.pix.error.linkKey"));
      setLoading(false);
    } finally {
      setLinkingKeyId("");
    }
  };

  const handleStartLink = async (pixKey) => {
    if ((isVirtualAccount || isOriginRail) && isAvailableToLinkPixKey(pixKey)) {
      await handleLinkToThisVirtual(pixKey);
      return;
    }
    setLinkingKey(pixKey);
    setSelectedTargetId("");
    await loadLinkTargets();
  };

  const handleConfirmLink = async () => {
    const keyId = resolveKeyId(linkingKey);
    if (!selectedInstance || !accountId || !keyId || !selectedTargetId || !canWritePix) return;
    setLinkingKeyId(keyId);
    setLoading(true);
    setFormError(null);
    try {
      await linkPixKey(selectedInstance, accountId, keyId, selectedTargetId);
      addToast({ type: "success", title: intl.get("baas.pix.keys.linkSuccess") });
      setLinkingKey(null);
      setSelectedTargetId("");
      await refreshKeys();
    } catch (error) {
      setFormError(error?.message || intl.get("baas.pix.error.linkKey"));
      setLoading(false);
    } finally {
      setLinkingKeyId("");
    }
  };

  const handleUnlink = async (pixKey) => {
    const keyId = resolveKeyId(pixKey);
    const originId = resolveOriginAccountId(pixKey, accountId);
    if (!selectedInstance || !originId || !keyId || loading || !canWritePix) return;
    setLinkingKeyId(keyId);
    setLoading(true);
    setFormError(null);
    try {
      await unlinkPixKey(selectedInstance, originId, keyId);
      addToast({ type: "success", title: intl.get("baas.pix.keys.unlinkSuccess") });
      await refreshKeys();
    } catch (error) {
      setFormError(error?.message || intl.get("baas.pix.error.unlinkKey"));
      setLoading(false);
    } finally {
      setLinkingKeyId("");
    }
  };

  const linkTargetOptions = useMemo(
    () =>
      linkTargets.map((item) => ({
        id: item.id,
        label: `${accountLabel(item)} (${item.type || "—"})`,
      })),
    [linkTargets],
  );

  const linkedKeys = useMemo(
    () => keys.filter((key) => isLinkedPixKey(key) && !isAvailableToLinkPixKey(key)),
    [keys],
  );
  const availableMatrizKeys = useMemo(
    () => keys.filter((key) => isAvailableToLinkPixKey(key)),
    [keys],
  );
  const ownedKeys = useMemo(
    () =>
      keys.filter(
        (key) => !isLinkedPixKey(key) && !isAvailableToLinkPixKey(key),
      ),
    [keys],
  );

  const emptyMessage =
    isVirtualAccount || isOriginRail
      ? intl.get("baas.pix.keys.emptyVirtual")
      : intl.get("baas.pix.keys.empty");

  const sectionMode = isVirtualAccount || isOriginRail;

  return (
    <Dialog
      open={open && canViewPix}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: MODAL_CLASS }}
    >
      <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-brand-dark-border">
        <div className="flex min-w-0 items-start gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#e7a73f]/15">
            <TbKey className="h-4 w-4 text-[#a66f1d]" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-brand-dark dark:text-gray-100">
              {intl.get("baas.accounts.detail.pixKeysTitle")}
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
              {sectionMode
                ? intl.get("baas.pix.keys.virtualModalSubtitle")
                : intl.get("baas.pix.subtitle")}
            </p>
          </div>
        </div>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={loading}
          aria-label={intl.get("baas.accounts.detail.close")}
        >
          <MdClose />
        </IconButton>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {formError ? (
          <p
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {formError}
          </p>
        ) : null}
        {isOriginRail ? (
          <p
            role="status"
            className="rounded-lg border border-[#e7a73f]/35 bg-[#e7a73f]/10 px-2.5 py-1.5 text-xs text-[#7a5418] dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10 dark:text-brand-gold-mid"
          >
            {intl.get("baas.pix.keys.originRailHint")}
          </p>
        ) : null}

        {isVirtualAccount ? (
          <p
            role="status"
            className="rounded-lg border border-[#e7a73f]/35 bg-[#e7a73f]/10 px-2.5 py-1.5 text-xs text-[#7a5418] dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10 dark:text-brand-gold-mid"
          >
            {intl.get("baas.pix.keys.virtualAccountHint")}
          </p>
        ) : null}

        {!isVirtualAccount && !isOriginRail && canCreateKey ? (
          <p className="rounded-lg border border-gray-200 bg-gray-50/80 px-2.5 py-1.5 text-xs text-gray-600 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 dark:text-gray-300">
            {intl.get("baas.pix.keys.originAccountLinkHint")}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {canWritePix && !isVirtualAccount && !isOriginRail ? (
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2 !h-8 !min-h-8 !px-3 !text-xs"
              onClick={handleCreateEvp}
              disabled={loading || !canCreateKey}
              title={
                createBlockedForShell
                  ? intl.get("baas.pix.keys.createDisabledShell")
                  : undefined
              }
            >
              <TbPlus className="h-3.5 w-3.5" aria-hidden />
              {intl.get("baas.pix.keys.createEvp")}
            </button>
          ) : null}
          <button
            type="button"
            className="btn-outline inline-flex items-center gap-2 !h-8 !min-h-8 !px-3 !text-xs"
            onClick={refreshKeys}
            disabled={loading}
          >
            <TbRefresh className="h-3.5 w-3.5" aria-hidden />
            {intl.get("baas.pix.refresh")}
          </button>
        </div>
        {createBlockedForShell && !isVirtualAccount && !isOriginRail ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.pix.keys.createDisabledShell")}
          </p>
        ) : null}

        {linkingKey ? (
          <div className="space-y-2 rounded-lg border border-[#e7a73f]/40 bg-[#e7a73f]/10 px-3 py-2.5 dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {intl.get("baas.pix.keys.linkTitle")}
            </p>
            <p
              className="truncate font-mono text-xs text-gray-600 dark:text-gray-300"
              title={linkingKey.key || resolveKeyId(linkingKey)}
            >
              {linkingKey.key || resolveKeyId(linkingKey)}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {intl.get("baas.pix.keys.linkHint")}
            </p>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                {intl.get("baas.pix.keys.linkTarget")}
              </span>
              <select
                className="input w-full"
                value={selectedTargetId}
                onChange={(event) => setSelectedTargetId(event.target.value)}
                disabled={loading || linkTargetsLoading}
              >
                <option value="">
                  {linkTargetsLoading
                    ? intl.get("baas.pix.keys.linkTargetsLoading")
                    : intl.get("baas.pix.keys.linkTargetPlaceholder")}
                </option>
                {linkTargetOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {!linkTargetsLoading && linkTargetOptions.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.pix.keys.linkTargetsEmpty")}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-primary !h-8 !min-h-8 !text-xs"
                onClick={handleConfirmLink}
                disabled={loading || !selectedTargetId}
              >
                {intl.get("baas.pix.keys.linkConfirm")}
              </button>
              <button
                type="button"
                className="btn-outline !h-8 !min-h-8 !text-xs"
                onClick={() => {
                  setLinkingKey(null);
                  setSelectedTargetId("");
                }}
                disabled={loading}
              >
                {intl.get("baas.pix.keys.linkCancel")}
              </button>
            </div>
          </div>
        ) : null}

        {sectionMode ? (
          <>
            <VirtualKeySection
              title={intl.get("baas.pix.keys.sectionLinked")}
              emptyMessage={intl.get("baas.pix.keys.sectionLinkedEmpty")}
              keys={linkedKeys}
              onDelete={handleDeleteKey}
              onLink={handleStartLink}
              onUnlink={handleUnlink}
              disabled={loading}
              canWrite={canWritePix}
              linkingKeyId={linkingKeyId}
            />
            <VirtualKeySection
              title={intl.get("baas.pix.keys.sectionMatriz")}
              emptyMessage={intl.get("baas.pix.keys.sectionMatrizEmpty")}
              keys={availableMatrizKeys}
              onDelete={handleDeleteKey}
              onLink={handleStartLink}
              onUnlink={handleUnlink}
              disabled={loading}
              canWrite={canWritePix}
              linkingKeyId={linkingKeyId}
            />
          </>
        ) : (
          <div className="app-card overflow-hidden">
            {ownedKeys.length === 0 && linkedKeys.length === 0 ? (
              <div className="app-empty-state m-3 !py-4 text-xs">{emptyMessage}</div>
            ) : (
              <GridHistoryContainer gridCols={PIX_KEYS_GRID_COLS} className="lg:!gap-x-3 lg:!px-3">
                <PixKeysHeader />
                {[...ownedKeys, ...linkedKeys].map((key) => (
                  <PixKeyRow
                    key={resolveKeyId(key) || key.key}
                    pixKey={key}
                    onDelete={handleDeleteKey}
                    onLink={handleStartLink}
                    onUnlink={handleUnlink}
                    disabled={loading}
                    canWrite={canWritePix}
                    linkingKeyId={linkingKeyId}
                    virtualMode={false}
                  />
                ))}
              </GridHistoryContainer>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default BaasAccountPixKeysModal;
